/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import customHttp from "../../services/customHTTP"; // Import custom HTTP client
import { CircularProgress, Box, Typography, Pagination, Alert } from "@mui/material"; // Material UI components for loading spinner and text
import MachineCard from "../../components/MachineCard/MachineCard";
import ContextFilterAndSort from "../../components/ContextFilterAndSort/ContextFilterAndSort";
import "./MyMachines.css";
import PageTitle from "../../components/PageTitle/PageTitle";
import { useSite } from "../../context/siteContext";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

const MyMachines = () => {
	const [machines, setMachines] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [pageSize, setPageSize] = useState(50); // Default page size
	const [currentPage, setCurrentPage] = useState(1); // Default current page
	const [totalPages, setTotalPages] = useState(0); // Total number of pages based on pageSize and totalItems
	const [machineSearchTerm, setMachineSearchTerm] = useState("");
	const { selectedSite, siteData, selectedSiteData, currencySymbol } = useSite();
	const [sortOption, setSortOption] = useState("");

	const sortOptions = ["Name Asc", "Name Desc", "Number Asc", "Number Desc"];

	useEffect(() => {
		setLoading(true);
		const fetchMachines = async () => {
			try {
				const reqBody = {
					site_number: selectedSite,
				};

				const response = await customHttp.post("/machines/bysiteid", reqBody);
				if (response.data.success) {
					const filteredMachines = response.data.data.filter(
						(machine, index, self) =>
							index === self.findIndex((m) => m.machine_number === machine.machine_number)
					);
					console.log(filteredMachines);
					setMachines(filteredMachines); // Set the filtered machines array
					setTotalPages(Math.ceil(filteredMachines.length / pageSize));
				} else {
					setError("Failed to fetch data: No success flag.");
				}
			} catch (err) {
				setError("Failed to fetch data: " + err.message);
			} finally {
				setLoading(false);
			}
		};

		if (selectedSite !== "") {
			fetchMachines();
			console.log(currencySymbol);
		}
	}, [pageSize, selectedSite, siteData]);

	const filteredMachines = machines.filter(
		(item) =>
			item.machine_name.toLowerCase().includes(machineSearchTerm.toLowerCase()) ||
			item.machine_number.toLowerCase().includes(machineSearchTerm.toLowerCase())
	);

	const handleSortChange = (value) => {
		setSortOption(value);

		if (value === "Name Asc") {
			setMachines((prevMachines) =>
				[...prevMachines].sort((a, b) => a.machine_name.trim().localeCompare(b.machine_name.trim()))
			);
		} else if (value === "Name Desc") {
			setMachines((prevMachines) =>
				[...prevMachines].sort((a, b) => b.machine_name.trim().localeCompare(a.machine_name.trim()))
			);
		} else if (value === "Number Asc") {
			setMachines((prevMachines) =>
				[...prevMachines].sort((a, b) => a.machine_number.trim().localeCompare(b.machine_number.trim()))
			);
		} else if (value === "Number Desc") {
			setMachines((prevMachines) =>
				[...prevMachines].sort((a, b) => b.machine_number.trim().localeCompare(a.machine_number.trim()))
			);
		}
	};

	const handleMachineSearchChange = (value) => {
		setMachineSearchTerm(value);
	};

	// If data is loading, display a loading spinner
	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
				<CircularProgress />
			</Box>
		);
	}

	// If there's an error, display the error message
	if (error) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
				<Alert severity="error" className="error-alert">
					{error}
				</Alert>
			</Box>
		);
	}

	// If no machines, display a message
	if (machines.length === 0 && !loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
				<Alert severity="info" className="error-alert">
					No Machines Found for the selected Site
				</Alert>
			</Box>
		);
	}

	// Paginate the machines based on pageSize and currentPage
	const startIndex = (currentPage - 1) * pageSize;
	const currentMachines = filteredMachines.slice(startIndex, startIndex + pageSize);

	return (
		<div className="machines-page">
			<PageTitle title="My Machines" />

			<Box className="machine-content-header">
				<Box>
					{/* <Typography variant="body1" fontSize="11px" sx={{ color: "#aaa" }}>
						
					</Typography> */}
					<Typography variant="body1" fontSize="11px" sx={{ color: "#aaa" }}>
						<b>**Note:</b> Machine 3D Model:{" "}
						<ViewInArOutlinedIcon fontSize="small" sx={{ margin: "0px 3px" }} /> | Change Parts 3D Model:{" "}
						<WidgetsOutlinedIcon fontSize="small" sx={{ margin: "0px 3px" }} /> | Machine Documentation:{" "}
						<DescriptionOutlinedIcon fontSize="small" sx={{ margin: "0px 3px" }} />
					</Typography>
				</Box>
				<Box sx={{ width: { xs: "100%", md: "20%" } }}>
					<ContextFilterAndSort
						value={machineSearchTerm}
						onSearchChange={handleMachineSearchChange}
						placeholder="Search Machines"
						options={sortOptions}
						selectedOption={sortOption}
						onSortChange={handleSortChange}
						showSearch={true}
						showSort={true}
					/>
				</Box>
			</Box>

			<div className="machines-list-container">
				{currentMachines.length === 0 ? (
					<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
						<Alert severity="info" sx={{ width: "fit-content" }}>
							No machines found.
						</Alert>
					</Box>
				) : (
					currentMachines.map((machine) => (
						<div key={machine.id}>
							<MachineCard key={machine.machine_number} product={machine} />
						</div>
					))
				)}
			</div>

			{/* Pagination controls */}
			<Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
				<Pagination
					count={totalPages}
					page={currentPage}
					onChange={(e, page) => setCurrentPage(page)}
					sx={{
						"& .Mui-selected": {
							backgroundColor: "var(--primary-main)",
							color: "white",
							background: "var(--primary-main)",
						},
					}}
				/>
			</Box>
		</div>
	);
};

export default MyMachines;
